import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useClients, useCreateClient, useExport, useKboLookup, useQuotaStatus } from '@/hooks/useApi';
import { getApiErrorCode } from '@/lib/errorCodes';
import toast from 'react-hot-toast';
import PageSkeleton from '@/components/PageSkeleton';
import Pagination from '@/components/Pagination';
import QuotaExceededBanner from '@/components/QuotaExceededBanner';
import type { KboEnterprise } from '@/types';

export default function ClientsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const typeFilter = searchParams.get('type') || '';
  const page = Number(searchParams.get('page')) || 1;
  const [showCreate, setShowCreate] = useState(false);

  // Debounce ref
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const debouncedSearch = searchParams.get('search') || '';

  const { data, isLoading, isFetching } = useClients({ page, type: typeFilter, search: debouncedSearch });
  const exportMutation = useExport();
  const createClient = useCreateClient();
  const quota = useQuotaStatus();

  // Create form state
  const [form, setForm] = useState({ name: '', email: '', vatNumber: '', type: 'company', country: 'BE', address: '', city: '', postalCode: '', phone: '', language: 'en' });
  const [vatLookup, setVatLookup] = useState('');
  const { data: kboData, isFetching: kboLoading } = useKboLookup(vatLookup);

  function applyKboData(kbo: KboEnterprise) {
    const street = [kbo.streetFr || kbo.streetNl, kbo.houseNumber, kbo.box].filter(Boolean).join(' ');
    setForm({
      ...form,
      name: kbo.nameFr || kbo.nameNl || kbo.commercialName || form.name,
      vatNumber: kbo.vatNumber,
      email: kbo.email || form.email,
      phone: kbo.phone || form.phone,
      address: street || form.address,
      city: kbo.municipalityFr || kbo.municipalityNl || form.city,
      postalCode: kbo.zipcode || form.postalCode,
      country: 'BE',
      type: kbo.typeOfEnterprise === '1' ? 'individual' : 'company',
    });
    toast.success(t('kbo.dataApplied'));
  }

  function setFilter(key: string, value: string) {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    p.delete('page');
    setSearchParams(p);
  }

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setFilter('search', value);
    }, 300);
  };

  function setPage(p: number) {
    const next = new URLSearchParams(searchParams);
    if (p <= 1) next.delete('page');
    else next.set('page', String(p));
    setSearchParams(next);
  }

  function handleCreateClient(e: React.FormEvent) {
    e.preventDefault();
    createClient.mutate(form, {
      onSuccess: () => { toast.success(t('clients.created')); setShowCreate(false); setForm({ name: '', email: '', vatNumber: '', type: 'company', country: 'BE', address: '', city: '', postalCode: '', phone: '', language: 'en' }); },
      onError: (error) => {
        const errorCode = getApiErrorCode(error);
        if (errorCode) {
          const localized = t(errorCode);
          if (localized !== errorCode) {
            toast.error(localized);
            return;
          }
        }
        toast.error(t('common.error'));
      },
    });
  }

  if (isLoading && !data) return <PageSkeleton />;

  return (
    <div>
      <div className="page-header">
        <h1 className="text-2xl font-bold">{t('clients.title')}</h1>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={() => exportMutation.mutate({ type: 'clients' })}>{t('common.export')}</button>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ {t('clients.addClient')}</button>
        </div>
      </div>

      {/* Proactive quota warning banner */}
      {quota.isLoaded && !quota.canCreateClient && (
        <div className="mb-4">
          <QuotaExceededBanner
            type="clients"
            used={quota.clientsUsed}
            limit={quota.clientsLimit}
            planLabel={quota.planLabel}
          />
        </div>
      )}

      <div className="card card-body mb-4 flex flex-col sm:flex-row gap-3">
        <input type="text" placeholder={t('common.search') + '...'} className="input flex-1"
          value={search} onChange={(e) => handleSearchChange(e.target.value)} />
        <select className="input w-auto" value={typeFilter} onChange={(e) => setFilter('type', e.target.value)}>
          <option value="">{t('clients.allTypes')}</option>
          {['company', 'individual', 'government', 'non_profit'].map((tp) => (
            <option key={tp} value={tp}>{t(`clients.types.${tp}`)}</option>
          ))}
        </select>
      </div>

      <div className="card overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>{t('clients.name')}</th>
              <th>{t('clients.email')}</th>
              <th>{t('clients.vatNumber')}</th>
              <th>{t('clients.country')}</th>
              <th>{t('clients.type_label')}</th>
            </tr>
          </thead>
          <tbody className={`divide-y divide-gray-100 transition-opacity ${isFetching ? 'opacity-50' : ''}`}>
            {isFetching && !data?.data?.length && Array.from({ length: 5 }).map((_, i) => (
              <tr key={i}>{Array.from({ length: 5 }).map((_, j) => <td key={j}><div className="skeleton h-4 w-24" /></td>)}</tr>
            ))}
            {data?.data?.map((c) => {
              const typeColor = c.type === 'company'
                ? 'row-blue'
                : c.type === 'individual'
                ? 'row-purple'
                : 'row-gray';
              return (
              <tr key={c.id} className={`cursor-pointer ${typeColor}`} onClick={() => navigate(`/clients/${c.id}`)}>
                <td><Link to={`/clients/${c.id}`} className="text-primary-600 font-medium hover:underline">{c.name}</Link></td>
                <td className="text-gray-600">{c.email || '—'}</td>
                <td className="text-gray-500 font-mono text-sm">{c.vatNumber || '—'}</td>
                <td className="text-gray-500">{c.country}</td>
                <td><span className="badge badge-gray">{t(`clients.types.${c.type}`)}</span></td>
              </tr>
              );
            })}
            {!isFetching && (!data?.data || data.data.length === 0) && (
              <tr><td colSpan={5} className="text-center text-gray-400 py-12">{t('common.noResults')}</td></tr>
            )}
          </tbody>
        </table>
        <Pagination meta={data?.meta} onPageChange={setPage} />
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="modal-backdrop">
          <form onSubmit={handleCreateClient} className="card w-full max-w-lg modal-sheet">
            <div className="card-header"><h2 className="text-lg font-bold">{t('clients.addClient')}</h2></div>
            <div className="card-body space-y-4">
              {/* Quota banner inside modal */}
              {quota.isLoaded && !quota.canCreateClient && (
                <QuotaExceededBanner
                  type="clients"
                  used={quota.clientsUsed}
                  limit={quota.clientsLimit}
                  planLabel={quota.planLabel}
                />
              )}
              {/* Client type toggle */}
              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                <button type="button"
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors ${form.type === 'individual' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  onClick={() => setForm({ ...form, type: 'individual', vatNumber: '' })}>
                  {t('clients.types.individual')}
                </button>
                <button type="button"
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors border-l border-gray-200 ${form.type === 'company' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  onClick={() => setForm({ ...form, type: 'company' })}>
                  {t('clients.types.company')}
                </button>
              </div>

              {/* VAT + KBO lookup (company only) */}
              {form.type === 'company' && (
                <div>
                  <label className="label">{t('clients.vatNumber')}</label>
                  <div className="flex gap-2">
                    <input type="text" className="input flex-1" value={form.vatNumber} onChange={(e) => setForm({ ...form, vatNumber: e.target.value })} placeholder="BE0123456789" />
                    <button type="button" className="btn btn-secondary text-xs whitespace-nowrap" disabled={!form.vatNumber || form.vatNumber.length < 10 || kboLoading}
                      onClick={() => setVatLookup(form.vatNumber)}>
                      {kboLoading ? '...' : t('kbo.findCompany')}
                    </button>
                  </div>
                  {kboData && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                      <div className="font-medium text-green-800 mb-1">{kboData.nameFr || kboData.nameNl}</div>
                      <div className="text-green-700 text-xs space-y-0.5 mb-2">
                        {kboData.streetFr && <div>{kboData.streetFr} {kboData.houseNumber}, {kboData.zipcode} {kboData.municipalityFr}</div>}
                        {kboData.activities?.[0] && <div>{t('kbo.activity')}: {kboData.activities[0].descriptionFr || kboData.activities[0].naceCode}</div>}
                      </div>
                      <button type="button" className="btn btn-primary w-full text-sm py-1.5" onClick={() => applyKboData(kboData)}>
                        {t('kbo.useCompany')}
                      </button>
                    </div>
                  )}
                  {vatLookup && !kboLoading && !kboData && (
                    <p className="text-xs text-gray-400 mt-1">{t('kbo.notFound')}</p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="label">{t('clients.name')} *</label>
                  <input type="text" className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div>
                  <label className="label">{t('clients.email')}</label>
                  <input type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div>
                  <label className="label">{t('clients.phone')}</label>
                  <input type="text" className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <label className="label">{t('clients.address')}</label>
                  <input type="text" className="input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                </div>
                <div>
                  <label className="label">{t('clients.city')}</label>
                  <input type="text" className="input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                </div>
                <div>
                  <label className="label">{t('clients.postalCode')}</label>
                  <input type="text" className="input" value={form.postalCode} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} />
                </div>
                <div>
                  <label className="label">{t('clients.country')}</label>
                  <input type="text" className="input" maxLength={2} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })} />
                </div>
                <div>
                  <label className="label">{t('clients.language')}</label>
                  <select className="input" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}>
                    {['en', 'fr', 'nl', 'es'].map((l) => (
                      <option key={l} value={l}>{t(`languages.${l}`)}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="card-body border-t flex gap-2 justify-end">
              <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>{t('common.cancel')}</button>
              <button type="submit" className="btn btn-primary" disabled={createClient.isPending || !quota.canCreateClient}>{t('common.save')}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
